import {
  Business,
  CalendarToday,
  Check,
  Close,
  Edit,
  Person,
  TrendingUp,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { assignOwnerToCreditRequest } from '../../../../../../../../lib/api/creditRequest';
import { cellFormat } from '../../../../../../../../lib/designSystem/Table/cellFormat';
import ExportDialog from '../../Recieved/components/create/components/Simulator/components/ExportDialog';
import { calculateAmortization } from '../../Recieved/components/create/components/Simulator/helpers/calculateAmortization';

interface OpenRequest {
  businessName: string;
  status: string;
  paymentDate: string | null;
  ownerName?: string;
  ownerId?: number;
  documents: any;
  workingDayActive: 'active' | 'inactive';
  id?: number;
}

interface CreditRequestStatusProps {
  openRequest: OpenRequest;
  audienceType: string;
  allowEditExecutive?: boolean;
  executivesPortfolio?: any[];
  executivesAll?: any[];
  onExecutiveChange?: () => Promise<void>;
  creditRequestId?: number;
  setSuccessOpen?: (message: string) => void;
  setFailureOpen?: (message: string) => void;
}

const statusSteps: any = {
  sent: {
    Aceptada:
      'Para que está solicitud sea activada, se debe realizar la nómina y luego su giro correspondiente.',
    Activa: 'La solicitud está activa',
  },
  received: {
    Aceptada:
      'Para que está solicitud sea activada, se debe realizar la nómina y luego su giro correspondiente.',
    Activa: 'La solicitud está activa',
    'En giro':
      'Para avanzar debes ir a la pestaña de giro y generar la nómina de la solicitud. Luego en pagos podrás completar el giro y activar la solicitud.',
  },
};

const CreditRequestStatus: React.FC<CreditRequestStatusProps> = ({
  openRequest,
  audienceType,
  allowEditExecutive = false,
  executivesPortfolio = [],
  executivesAll = [],
  onExecutiveChange,
  creditRequestId,
  setSuccessOpen,
  setFailureOpen,
}) => {
  const dispatch = useDispatch();
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [amortizationSchedule, setAmortizationSchedule] = useState<any>(
    openRequest.documents,
  );
  const [isEditingExecutive, setIsEditingExecutive] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState<number | string>(
    openRequest.ownerId || '',
  );
  const [loadingExecutive, setLoadingExecutive] = useState(false);

  const handleExportSimulation = () => {
    setOpenExportDialog(true);
  };

  useEffect(() => {
    calculateAmortization({
      creditDetails: openRequest,
      setAmortizationSchedule,
      workingDayActive: openRequest.workingDayActive,
    });
  }, [openRequest]);

  useEffect(() => {
    setSelectedExecutive(openRequest.ownerId || '');
  }, [openRequest.ownerId]);

  const getExecutiveName = (executiveId: number | string) => {
    if (!executiveId) return '';

    const portfolioExecutive = executivesPortfolio?.find(
      (exec: any) => exec.id === executiveId,
    );
    if (portfolioExecutive) {
      return portfolioExecutive.name;
    }

    const allExecutive = executivesAll?.find(
      (exec: any) => exec.id === executiveId,
    );
    if (allExecutive) {
      return allExecutive.name;
    }

    return '';
  };

  const handleEditExecutive = () => {
    setIsEditingExecutive(true);
  };

  const handleCancelEditExecutive = () => {
    setIsEditingExecutive(false);
    setSelectedExecutive(openRequest.ownerId || '');
  };

  const handleSaveExecutive = async () => {
    if (!selectedExecutive || !creditRequestId) {
      const errorMsg = 'Por favor selecciona un ejecutivo';
      if (setFailureOpen) {
        setFailureOpen(errorMsg);
      }
      return;
    }

    setLoadingExecutive(true);
    try {
      await assignOwnerToCreditRequest(
        dispatch,
        creditRequestId,
        Number(selectedExecutive),
      );
      const successMsg = 'Ejecutivo actualizado correctamente';
      setIsEditingExecutive(false);
      if (setSuccessOpen) {
        setSuccessOpen(successMsg);
      }
      if (onExecutiveChange) {
        await onExecutiveChange();
      }
    } catch (error: any) {
      console.error('Error updating executive:', error);
      const errorMsg =
        error?.message ||
        'Error al actualizar el ejecutivo. Intenta nuevamente.';
      if (setFailureOpen) {
        setFailureOpen(errorMsg);
      }
    } finally {
      setLoadingExecutive(false);
    }
  };

  // Componente para información del cliente
  const ClientInfoCard = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={4}
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Business color="primary" sx={{ fontSize: 20 }} />
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
            >
              Cliente
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.primary">
            {openRequest.businessName}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <TrendingUp color="primary" sx={{ fontSize: 20 }} />
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
            >
              Estado
            </Typography>
          </Stack>
          <Typography variant="body1">
            {cellFormat(openRequest.status, 'status')}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <CalendarToday color="primary" sx={{ fontSize: 20 }} />
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
            >
              Vencimiento
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.primary">
            {openRequest.paymentDate
              ? cellFormat(openRequest.paymentDate, 'date')
              : 'Sin fecha'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Person color="primary" sx={{ fontSize: 20 }} />
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
            >
              Ejecutivo
            </Typography>
            {allowEditExecutive && !isEditingExecutive && (
              <IconButton
                size="small"
                onClick={handleEditExecutive}
                sx={{ p: 0.5 }}
              >
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Stack>
          {!isEditingExecutive ? (
            <Typography variant="body1" color="text.primary">
              {openRequest.ownerName}
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedExecutive}
                  onChange={(e) => setSelectedExecutive(e.target.value)}
                  disabled={loadingExecutive}
                  displayEmpty
                  renderValue={(value) => {
                    if (value) {
                      const name = getExecutiveName(value);
                      return name || 'Seleccionar ejecutivo';
                    }
                    return 'Seleccionar ejecutivo';
                  }}
                >
                  <ListSubheader sx={{ fontWeight: 'bold' }}>
                    Cliente en cartera ({executivesPortfolio?.length || 0})
                  </ListSubheader>
                  {executivesPortfolio?.map((executive: any) => (
                    <MenuItem key={executive.id} value={executive.id}>
                      {executive.name}
                    </MenuItem>
                  ))}
                  <ListSubheader sx={{ fontWeight: 'bold' }}>
                    Todos ({executivesAll?.length || 0})
                  </ListSubheader>
                  {executivesAll?.map((executive: any) => (
                    <MenuItem key={executive.id} value={executive.id}>
                      {executive.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                color="success"
                onClick={() => void handleSaveExecutive()}
                disabled={loadingExecutive}
              >
                <Check sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={handleCancelEditExecutive}
                disabled={loadingExecutive}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );

  const NextStepsCard = () =>
    statusSteps[audienceType]?.[openRequest.status] && (
      <Alert
        severity="info"
        icon={<TrendingUp />}
        variant="outlined"
        sx={{
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Pasos a seguir
        </Typography>
        <Typography variant="body2">
          {statusSteps[audienceType][openRequest.status]}
        </Typography>
      </Alert>
    );

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Resumen
        </Typography>
        <Typography
          onClick={handleExportSimulation}
          variant="caption"
          color="primary"
          sx={{
            cursor: 'pointer',
            fontWeight: 'bold',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Exportar
        </Typography>
      </Stack>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <ClientInfoCard />
        <NextStepsCard />
      </Stack>

      {openExportDialog && (
        <ExportDialog
          open={openExportDialog}
          setOpen={setOpenExportDialog}
          creditDetails={openRequest}
          amortizationSchedule={amortizationSchedule}
        />
      )}
    </Box>
  );
};

export default CreditRequestStatus;
